import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  ArrowLeft,
  ListDashes,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { LEGAL_PAGES } from '../data/legal';

// --- helpers -------------------------------------------------------

const slugifyHeading = (text, idx) =>
  `section-${idx + 1}-${(text || '')
    .toString()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)}`;

const scrollToId = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
  window.scrollTo({ top: y, behavior: 'smooth' });
  history.replaceState(null, '', `#${id}`);
};

// --- section body renderer ----------------------------------------

const SectionBody = ({ section }) => {
  // Legacy: `body` is a plain string → render as a single paragraph.
  if (typeof section.body === 'string') {
    return (
      <p className="text-sm sm:text-base text-[#3D512D] leading-9">
        {section.body}
      </p>
    );
  }

  // New: `blocks` array with typed blocks.
  const blocks = section.blocks || [];
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        if (b.type === 'paragraph') {
          return (
            <p
              key={i}
              className="text-sm sm:text-base text-[#3D512D] leading-9"
            >
              {b.text}
            </p>
          );
        }
        if (b.type === 'bullets') {
          return (
            <ul key={i} className="space-y-2.5 pr-2">
              {b.items.map((item, j) => (
                <li
                  key={j}
                  className="text-sm sm:text-base text-[#3D512D] leading-9 relative pr-5"
                >
                  <span
                    className="absolute top-3.5 right-0 w-1.5 h-1.5 rounded-full bg-[#4A641F]"
                    aria-hidden
                  />
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === 'numbered') {
          return (
            <ol key={i} className="space-y-4">
              {b.items.map((item, j) => (
                <li
                  key={j}
                  className="text-sm sm:text-base text-[#3D512D] leading-9 flex items-start gap-3"
                >
                  <span
                    className="shrink-0 mt-1 w-7 h-7 rounded-full bg-[#F5F2EC] text-[#1F3D2B] text-xs font-bold flex items-center justify-center"
                    aria-hidden
                  >
                    {j + 1}
                  </span>
                  <span>
                    <strong className="text-[#1F3D2B]">{item.title}: </strong>
                    {item.text}
                  </span>
                </li>
              ))}
            </ol>
          );
        }
        return null;
      })}
    </div>
  );
};

// --- main page ----------------------------------------------------

const LegalPage = () => {
  const { slug } = useParams();
  const page = LEGAL_PAGES[slug];

  if (!page) {
    return <Navigate to="/" replace />;
  }

  const today = new Date().toLocaleDateString('ar-SA', {
    year: 'numeric',
    month: 'long',
  });

  // Build TOC data from the page's sections.
  const toc = page.sections.map((s, i) => ({
    id: slugifyHeading(s.heading, i),
    label: s.heading,
    index: i + 1,
  }));

  return (
    <main
      data-testid={`legal-page-${page.slug}`}
      className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
      dir="rtl"
    >
      {/* Hero */}
      <section className="bg-[#1F3D2B] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#D8E0C9] hover:text-white mb-5 transition-colors"
            >
              ← العودة للصفحة الرئيسية
            </Link>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm mb-5">
              <ShieldCheck size={16} weight="duotone" />
              {page.badge}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-4 tracking-tight">
              {page.title}
            </h1>
            <p className="text-xs sm:text-sm text-[#A3B5A0] mb-5" dir="ltr">
              {page.titleEn}
            </p>
            <p className="text-sm text-[#D8E0C9] leading-9 max-w-3xl">
              {page.intro}
            </p>
            <p className="text-[11px] text-[#7E916A] mt-6">
              آخر تحديث: {today}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content + In-page TOC */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_280px] gap-10">
          {/* Article body */}
          <article className="bg-white border border-[#E6E0D6] rounded-[24px] p-6 sm:p-10 shadow-sm">
            {page.sections.map((s, i) => {
              const id = slugifyHeading(s.heading, i);
              return (
                <motion.section
                  id={id}
                  key={id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04, duration: 0.4 }}
                  className="mb-10 last:mb-0 scroll-mt-24"
                >
                  <h2 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic'] flex items-baseline gap-2">
                    <span className="text-[#92A082] text-xs font-bold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {s.heading}
                  </h2>
                  <SectionBody section={s} />
                </motion.section>
              );
            })}

            <div className="mt-10 pt-6 border-t border-[#E6E0D6]">
              <p className="text-xs text-[#92A082] leading-7">
                لأي استفسار قانوني أو ممارسة حقوقك المتعلقة بالبيانات
                الشخصية، يرجى زيارة صفحة{' '}
                <Link
                  to="/contact"
                  className="text-[#4A641F] hover:underline"
                >
                  «اتصل بنا»
                </Link>
                .
              </p>
            </div>
          </article>

          {/* Sidebar — Table of Contents (in-page navigation) */}
          <aside className="lg:sticky lg:top-28 self-start">
            <div
              className="bg-white border border-[#E6E0D6] rounded-[20px] p-6"
              data-testid="legal-toc"
            >
              <h3 className="text-sm font-bold text-[#1F3D2B] mb-4 flex items-center gap-2 font-['IBM_Plex_Sans_Arabic']">
                <ListDashes
                  size={18}
                  weight="duotone"
                  className="text-[#4A641F]"
                />
                فهرس المحتوى
              </h3>
              <ul className="space-y-2.5">
                {toc.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      onClick={scrollToId(t.id)}
                      data-testid={`toc-link-${t.id}`}
                      className="text-xs sm:text-[13px] inline-flex items-start gap-2 text-[#5D6E55] hover:text-[#1F3D2B] transition-colors leading-7"
                    >
                      <span className="text-[#92A082] font-bold shrink-0">
                        {String(t.index).padStart(2, '0')}
                      </span>
                      <span>{t.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-[#E6E0D6]">
                <Link
                  to="/contact"
                  className="text-xs text-[#4A641F] inline-flex items-center gap-1 hover:underline"
                >
                  <EnvelopeSimple size={14} />
                  استفسار قانوني؟ اتصل بنا
                  <ArrowLeft size={12} className="rtl-flip" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default LegalPage;
